import React, { useEffect } from "react";
import { withGlobal } from "reactn";

import { Container } from "@jsluna/grid";
import { trackEvent } from "@jsainsburyplc/id-ui-common";
import missionConfig from "missionConfig";

import {
  INVALID_CREDENTIALS,
  ACCOUNT_LOCKED,
  INVALID_CAPTCHA,
  PASSWORD_EXPIRED,
  ACCOUNT_INACTIVE,
  DENIED_IOVATION_RESPONSE,
  WEBAUTHN_ASSERTION_FAILED,
} from "constants/codes";
import { DATA_ERROR } from "constants/login";
import { MFA_CODE_SENT } from "constants/mfaAndOtp";
import { categoryTypes, actionTypes } from "constants/tracking";
import useCookies from "hooks/useCookies";
import { isIovationEnabled } from "utils/env";

import ActivateAccount from "components/Login/Notifications/ActivateAccount";
import PasswordExpiredNotification from "components/Login/Notifications/PasswordExpiredNotification";
import AccountLockedNotification from "components/Login/Notifications/AccountLockedNotification";
import AccountBlockedNotification from "components/Login/Notifications/AccountBlockedNotification";
import AccountBlockedIovation from "components/AccountBlockedIovation";
import LoginForm from "components/Login/LoginForm";
import { WEBAUTHN_ASSERTION_ERROR } from "constants/messages";
import useDigitalDataTracking from "hooks/useDigitalTrack";

const isIovationEnabledEnv = isIovationEnabled();

const LoginPage = ({ missionName, missionTitle, missionNumber }) => {
  const rootElem = document.querySelector("#root");

  const ERROR_CODE = parseInt(rootElem.getAttribute(DATA_ERROR));

  const { email, loginChallenge } = useCookies();

  useDigitalDataTracking(
    "web:groceries:authentication:login",
    "authentication",
    "authLogin",
    "view"
  );

  useEffect(() => {
    sessionStorage.removeItem(MFA_CODE_SENT);
    return () => {
      if (rootElem.getAttribute(DATA_ERROR)) {
        rootElem.removeAttribute(DATA_ERROR);
      }
    };
  }, [rootElem]);

  useEffect(() => {
    if (ERROR_CODE) {
      trackEvent({
        category: categoryTypes.LOGIN,
        action: actionTypes.ERROR,
        label: `Error code: ${ERROR_CODE}`,
      });
    }
  }, [ERROR_CODE]);

  if (ERROR_CODE) {
    switch (ERROR_CODE) {
      case INVALID_CREDENTIALS:
        let errorMessage =
          "That email or password doesn’t look right. Please try again or reset your password below. Too many failed attempts will lock your account.";
        return (
          <LoginForm
            missionName={missionName}
            errorMessage={errorMessage}
            loginChallenge={loginChallenge}
            email={email}
            isIovationEnabled={isIovationEnabledEnv}
          />
        );
      case WEBAUTHN_ASSERTION_FAILED:
        errorMessage = WEBAUTHN_ASSERTION_ERROR;
        return (
          <LoginForm
            missionName={missionName}
            errorMessage={errorMessage}
            hasResetPasswordLink={false}
            loginChallenge={loginChallenge}
            email={email}
            isIovationEnabled={isIovationEnabledEnv}
          />
        );
      case ACCOUNT_INACTIVE:
        return (
          <ActivateAccount
            errorMessage={errorMessage}
            email={email}
            missionName={missionName}
          />
        );
      case PASSWORD_EXPIRED:
        return <PasswordExpiredNotification />;
      case ACCOUNT_LOCKED.find((elem) => elem === ERROR_CODE):
        return <AccountLockedNotification missionName={missionName} />;
      case DENIED_IOVATION_RESPONSE:
        return (
          <Container soft size="xs">
            <AccountBlockedIovation />
          </Container>
        );
      case INVALID_CAPTCHA.find((elem) => elem === ERROR_CODE):
        return (
          <LoginForm
            missionName={missionName}
            hasResetPasswordLink={false}
            errorMessage="Unfortunately, there has been an error in the request. Please try again later"
            email={email}
            loginChallenge={loginChallenge}
            isIovationEnabled={isIovationEnabledEnv}
          />
        );
      default:
        return (
          <AccountBlockedNotification
            missionName={missionName}
            number={missionNumber}
          />
        );
    }
  } else {
    return (
      <LoginForm
        title={missionConfig.showTitle ? missionTitle : "Log in"}
        missionName={missionName}
        loginChallenge={loginChallenge}
        isIovationEnabled={isIovationEnabledEnv}
      />
    );
  }
};

export default withGlobal((global) => ({
  missionName: global.readable_mission_name,
  missionTitle: global.title,
  missionNumber: global.call_centre_number,
}))(LoginPage);
